/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import * as mobx from 'mobx';
import { types, getEnv, isAlive } from 'mobx-state-tree';
import _ from 'underscore';
import ThumbnailsModel from './thumbnails/ThumbnailsModel';
import { replaceArray } from 'util/ArrayOperations';

const CONTEXT_MENU_STRUCTURE = [
	['copy','paste'],
	null,	//separator
	['duplicate','show','hide','compare'],
	null,	//separator
	['deleteCard']
];

/**
 *
 * @private
 * @param {string[]} currentItems - ids of the current displayed items
 * @param {string[]} placeHolders - ids of the items in the placeHolder
 * @param {integer} firstPlaceHolderIndex - index in the first placeHolder item in the list of currentItems
 * @param {string} targetCardId - id of the card/item when the placeHolder is to go
 * @returns {string[]} - new array of items id where the the placeholder takes the position of the specified card
 * @example
 *  // target card is before the first one in the placeholder; target card pushed to the right
 *  buildNewDisplayedItems(['a', 'b', 'c', 'd'], ['c'], 2, 'a') returns ['c', 'a', 'b', 'd']
 *  // target card is after the first element in the placeholder, target card pushed to the left
 *  buildNewDisplayedItems(['a', 'b', 'c', 'd'], ['b'], 1, 'c') returns ['a', 'c', 'b', 'd']
 *  //multiple items in the placeholders
 *  buildNewDisplayedItems(['a', 'b', 'c', 'd', 'e'], ['b', 'e'], 1, 'c') returns ['a', 'c', 'b', 'e', 'd']
 *
 */
const buildNewDisplayedItems = (currentItems, placeHolders, firstPlaceHolderIndex, targetCardId) => {
	let displayedItems = [];
	for (let j = 0; j < currentItems.length; j++) {
		const itemId = currentItems[j];
		if (placeHolders.indexOf(itemId) < 0) {
			if (itemId !== targetCardId) {
				displayedItems.push(itemId);
			} else {
				if (j >= firstPlaceHolderIndex) {
					displayedItems.push(itemId);
					displayedItems = displayedItems.concat(placeHolders);
				} else {
					displayedItems = displayedItems.concat(placeHolders);
					displayedItems.push(itemId);
				}
			}
		}
	}
	return displayedItems;
};



export default types
	.model('CardListViewModel', {
		thumbnails: ThumbnailsModel
	})
	.volatile(() => ({
		_isAlive: isAlive,
		isDragged: false,
		selection: null,
		placeholders: [],
		items: mobx.observable([]),
		layoutModel: null,
		exploreVM: null,
		contextMenu: null,
		_mobxDisposeEvents: []
	}))
	.views((self) => ({
		/**
		 * Returns whether there are card(s) selected or not.
		 * @param {string} id - id of card to test whether it is selected
		 * @return {boolean}
		 */
		hasSelectedCards() {
			return self.selection.hasSelections();
		},
		/**
		 * Determine if a card is selected
		 * @param {string} id - id of card to test whether it is selected
		 * @return {boolean}
		 */
		isCardSelected(id) {
			return self.selection.isSelected(id);
		},
		isEditMode() {
			return self.selection.items.length > 1 || (self.hasSelectedCards() && self.selection.items[0] !== self.exploreVM.displayedCard);
		},
		shouldShowContextMenu(ids) {
			ids = ids || self.selection.items;

			return !!self.contextMenu && _.intersection(ids, self.contextMenu).length === ids.length;
		},
		isStartingPointsDisplayed() {
			return self.exploreVM.isStartingPointsDisplayed();
		},
		getSelectedCards() {
			return self.selection.items.map((id) => self.layoutModel.findModel(id));
		},
		getContextMenuItems(cards) {
			cards = cards || self.getSelectedCards();

			const actionDescriptors = getEnv(self).cardActions.getActions(cards);

			return CONTEXT_MENU_STRUCTURE.map(
				menuItems => {
					if(!menuItems) {
						return {};	//Represents a separator
					}

					const items = menuItems.map(
						menuItem => actionDescriptors.find(descriptor => descriptor.id === menuItem)
					).filter(
						actionDescriptor => !!actionDescriptor
					).map(
						actionDescriptor => ({
							id: actionDescriptor.id,
							value: actionDescriptor.id,
							label: actionDescriptor.label
						})
					);

					return { items };
				}
			);

		},
		performAction(actionId, cards) {
			cards = cards || self.getSelectedCards();

			const actionDescriptor = getEnv(self).cardActions.getAction(actionId);
			actionDescriptor.action(cards);
		},
		/**
		 * Return card item array
		 */
		getCards() {
			const depModelsAlive = () => {
				return (self._isAlive(self.layoutModel) && self._isAlive(self.exploreVM));
			};
			if(!depModelsAlive()) {
				return null;
			}
			return self.items.map(id => {
				if (self.placeholders.indexOf(id) !== -1) {
					return { id: 'placeholder' };
				}
				return self.layoutModel.findModel(id);
			});
		},
		/**
		 * Find the index of the child in the item array
		 * @param {Object} item with an id attribute
		 */
		getItemIndex(id) {
			if(self.items) {
				return self.items.indexOf(id);
			}
			return -1;
		},
		/**
		 * @param {String} id of the Card
		 * Return Card object
		 */
		getCard(id) {
			return self.layoutModel.findModel(id);
		},
		isDragging() {
			return self.isDragged;
		}
	}))
	.actions((self) => ({
		beforeDestroy() {
			self._mobxDisposeEvents.forEach(dispose => dispose());
		},
		setModelChecker( isAlive ) {
			self._isAlive = isAlive;
		},
		setLayoutModel(layoutModel) {
			self.layoutModel = layoutModel;
			self.exploreVM = layoutModel.exploreVM;

			self._mobxDisposeEvents.push(
				mobx.reaction(() => {
					return self.layoutModel.items.map(item => item.id);
				}, (latestItemIds) => {
					setItems(self.items, latestItemIds);
				}, {
					fireImmediately: true
				})
			);

			// Sync the selection with the displayed card.
			self._mobxDisposeEvents.push(mobx.reaction(() => {
				return self.exploreVM.displayedCard;
			}, () => {
				if (self.contextMenu && self.contextMenu[0] !== self.exploreVM.displayedCard) {
					self.showContextMenu(null);
				}
			}, { delay:200 } ));
		},
		setSelectionModel(selectionModel) {
			self.selection = selectionModel;
		},
		/**
		 * Modify current selection by toggling one card
		 * @param {string} cardId - id of card to select
		 * @param {object} [options]
		 * @param {boolean} [options.multiselect=false] - indicates the user has depressed the multiselect key (cmd)
		 * @param {boolean} [options.rangeselect=false] - indicates the user has depressed the multiselect key (shift)
		 */
		selectCard(cardId, options = {}) {
			if (self.isStartingPointsDisplayed()) {
				//When starting points was selected, (or not multi-select - see below)
				//change displayed card (reaction will update selected card)
				self.exploreVM.setDisplayedToCard(cardId);
			} else if (options.multiselect) {
				//When multi-select, toggle selection of card - unless this would cause
				//no cards to be selected - in this case, do nothing
				if (!self.selection.isSelected(cardId) || self.selection.items.length !== 1) {
					self.selection.select(cardId, { control: options.multiselect });
				}
			} else if (options.rangeselect) {
				//When range select, update selection accordingly
				self.selection.select(cardId, { shift: options.rangeselect });
			} else {
				//When not multi-select, (or starting points was selected - see above)
				//change displayed card (reaction will update selected card)
				self.exploreVM.setDisplayedToCard(cardId);
			}
			self.restoreToolbarIfHidden();
		},
		selectAll() {
			self.selection.selectAll();
		},
		clearSelection() {
			if(self.exploreVM.isStartingPointsDisplayed()) {
				self.selection.clear();
			} else {
				self.selection.select(self.exploreVM.getDisplayedCard());
			}
		},
		beginDrag(ids) {
			self.isDragged = true;
			self.placeholders = ids;
		},
		/**
		 * moves the place holder to the position of the passed card id, order/display is unchanged if the id is unknown or is part of the placeholder
		 * @param {String} targetCardId id of the target card, new location of the ones in the placeholder
		 */
		movePlaceHolderToCard(targetCardId) {
			const ids = self.placeholders;
			const index = self.getItemIndex(targetCardId);

			if (index < 0) {
				return;
			}

			if (!ids.length) {
				return;
			}
			const firstPlaceHolderIndex = self.getItemIndex(ids[0]);

			if (firstPlaceHolderIndex < 0) {
				return;
			}

			if (ids.indexOf(targetCardId) < 0) {
				const displayedItems = buildNewDisplayedItems(self.items, ids, firstPlaceHolderIndex, targetCardId);
				setItems(self.items, displayedItems);
			}
		},
		endDrag() {
			self.isDragged = false;
			self.placeholders = [];

			const newItemsArray = self.items.map((id) => self.layoutModel.findModel(id));
			const payloadData = {
				undoRedoTransactionId: _.uniqueId('CardList')
			};
			self.layoutModel.replaceArray(newItemsArray, 'CardList', payloadData);
		},
		selectStartingPoints() {
			self.exploreVM.setDisplayedToStartingPoints();
			self.restoreToolbarIfHidden();
		},
		showContextMenu(ids) {
			self.contextMenu = ids;
		},
		clearContextMenu() {
			self.contextMenu = null;
		},
		resetLoadingCards() {
			if (self._isAlive(self.exploreVM)) {
				self.exploreVM.resetThumbnailsQueue();
			}
		},
		resetCardNotification() {
			self.exploreVM.notification.setListVisibility(false);
			self.exploreVM.notification.setActive(false);
		},
		loadCards(cardIds) {
			// only load cards that are not selected
			const nonSelectedCardIds = cardIds
				.filter(cardId => !self.isCardSelected(cardId));
			self.exploreVM.thumbnails.queue(self.removePlaceholderCards(nonSelectedCardIds));
		},
		removePlaceholderCards(cardIds) {
			const cards = cardIds.map(id => self.layoutModel.findModel(id));
			const nonBlankCardIds = [];
			cards && cards.forEach(card => {
				const layout = card.cardModelUtil.getLayouts(card, card.boardModel);
				const hasWidget = layout.length > 0 && layout[0].hasOwnProperty('widgetLayout') || false;
				hasWidget && nonBlankCardIds.push(card.id);
			});
			return nonBlankCardIds;
		},
		/**
		 * Restore toolbar if it is hidden
		 */
		restoreToolbarIfHidden(){
			// collapses the filter dock if toolbar is hidden
			if(self.exploreVM.toolbar && self.exploreVM.toolbar.getIsHidden()){
				self.layoutModel.getDashboardApi().triggerDashboardEvent('filterDock:collapse');
			}
		},

		/**
		 * Synchronize thumbnails
		 *
		 * @param {string|string[]|Widget[]} widgets - widget(s) to synchronize
		 *
		 * @return {Promise} Promise to be resolved when the synchronize operations is completed
		 */
		syncThumbnails(widgets, options = { idle: true } ) {
			const getThumbnails = getEnv(self).getThumbnails;
			if (!getThumbnails) {
				return Promise.resolve();
			}
			return getThumbnails(widgets, options)
				.then(thumbnails => {
					self.thumbnails.addItems(
						thumbnails.map(({ data, error, warning, widgetId, type }) => {
							return {
								widgetId,
								type,
								data,
								error: error && error.message ? error : undefined,
								warning: warning && warning.message ? warning : undefined
							};
						})
					);
				});
		},

		/**
		 * Synchronize initial thumbnails
		 *
		 * @return {Promise} Promise to be resolved when the synchronize operations is completed
		 */
		syncInitialThumbnails() {
			return self.syncThumbnails(undefined,  { idle: false });
		},

		/**
		 * Check whether the card with a specific id contains a blank widget or not
		 * @param {string} cardId - card id
		 * @return {boolean} TRUE if the card contains a blank widget and FALSE if not
		 */
		containsBlankWidget(cardId) {
			return self.exploreVM.containsBlankWidget(cardId);
		},

		isCardNew(cardId) {
			return self.exploreVM.isCardNew(cardId);
		}
	}));

function setItems(itemsArray, newValues) {
	replaceArray(itemsArray, newValues);
}



// WEBPACK FOOTER //
// ./src/js/components/cards/CardListViewModel.js